import { reactive } from 'vue';
import { doc, onSnapshot, runTransaction } from 'firebase/firestore';
import { db } from '@/firebaseConfig';

const controllers = new Map();
const AUTOSAVE_MS = 600;

function createController(userId) {
  const reference = doc(db, 'users', userId);
  const state = reactive({
    note: '', loaded: false, loading: true, dirty: false,
    saving: false, saved: false, loadError: '', saveError: '',
    conflict: false, remoteNote: '',
  });
  let savedNote = '';
  let writingNote = '';
  let writingAcknowledged = false;
  let timer = null;
  let pending = null;
  let unsubscribe = null;
  let listenerVersion = 0;
  let users = 0;

  const clearTimer = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };
  const forgetIfClean = () => {
    // Retain failed drafts without listeners/timers so reopening allows a retry.
    if (!users && !pending && !state.dirty && !state.conflict && controllers.get(userId) === controller) {
      controllers.delete(userId);
    }
  };
  const stopListening = () => {
    listenerVersion += 1;
    if (unsubscribe) unsubscribe();
    unsubscribe = null;
  };
  const acceptRemote = remoteNote => {
    if (state.note !== remoteNote) state.saved = false;
    savedNote = remoteNote;
    state.note = remoteNote;
    state.dirty = false;
    state.conflict = false;
    state.remoteNote = '';
    state.saveError = '';
    clearTimer();
  };
  const showConflict = remoteNote => {
    if (state.note === remoteNote) {
      acceptRemote(remoteNote);
      return;
    }
    state.conflict = true;
    state.remoteNote = remoteNote;
    state.dirty = true;
    state.saved = false;
    state.saveError = '';
    clearTimer();
  };
  const listen = () => {
    stopListening();
    state.loading = true;
    state.loadError = '';
    const version = listenerVersion;
    const fail = () => {
      if (version !== listenerVersion) return;
      state.loading = false;
      state.loadError = 'Impossibile caricare le note. Riprova.';
      clearTimer();
    };
    try {
      unsubscribe = onSnapshot(reference, { includeMetadataChanges: true }, snapshot => {
        if (version !== listenerVersion) return;
        if (!snapshot.exists()) {
          state.loaded = false;
          state.loading = false;
          state.loadError = 'Utente non trovato. Impossibile caricare le note.';
          clearTimer();
          return;
        }
        const remoteNote = String(snapshot.data().expiringListNote || '');
        // Pending local echoes cannot establish the confirmed comparison base.
        if (snapshot.metadata?.hasPendingWrites) return;
        state.loaded = true;
        state.loading = false;
        state.loadError = '';
        if (state.saving) {
          // The transaction owns its base until acknowledged. A later external
          // version stays visible as a conflict instead of replacing our draft.
          if (remoteNote === writingNote) {
            writingAcknowledged = true;
            state.conflict = false;
            state.remoteNote = '';
          } else if (remoteNote !== savedNote || writingAcknowledged) showConflict(remoteNote);
          return;
        }
        if (state.dirty || state.conflict) {
          if (remoteNote === state.note) acceptRemote(remoteNote);
          else if (state.conflict || remoteNote !== savedNote) showConflict(remoteNote);
          return;
        }
        acceptRemote(remoteNote);
      }, fail);
    } catch (error) {
      fail();
    }
  };

  const flush = () => {
    clearTimer();
    if (pending) return pending;
    if (!state.loaded || state.loading || state.loadError || !state.dirty || state.conflict) return Promise.resolve();
    // One writer per user, also when a row unmounts during an earlier write.
    pending = Promise.resolve().then(async () => {
      try {
        while (state.dirty && !state.conflict && state.loaded && !state.loading && !state.loadError) {
          clearTimer();
          writingNote = state.note;
          writingAcknowledged = false;
          const expectedNote = savedNote;
          state.saving = true;
          state.saved = false;
          state.saveError = '';
          try {
            await runTransaction(db, async transaction => {
              const snapshot = await transaction.get(reference);
              if (!snapshot.exists()) {
                throw Object.assign(new Error('User not found'), { code: 'user-note-missing' });
              }
              if (!state.loaded || state.loading || state.loadError) {
                throw Object.assign(new Error('Notes unavailable'), { code: 'user-note-unavailable' });
              }
              const remoteNote = String(snapshot.data().expiringListNote || '');
              // The expected base stays fixed even if Firestore retries this
              // transaction after another editor commits a concurrent change.
              if (remoteNote !== expectedNote && remoteNote !== writingNote) {
                throw Object.assign(new Error('Note changed'), {
                  code: 'user-note-conflict', remoteNote,
                });
              }
              if (remoteNote !== writingNote) transaction.update(reference, {
                expiringListNote: writingNote,
                updatedAt: new Date().toISOString(),
              });
            });
          } catch (error) {
            state.dirty = true;
            if (error.code === 'user-note-conflict') {
              showConflict(error.remoteNote);
            } else if (error.code === 'user-note-missing') {
              state.loaded = false;
              state.loadError = 'Utente non trovato. Impossibile caricare le note.';
            } else if (error.code !== 'user-note-unavailable') {
              state.saveError = 'Salvataggio non riuscito. La nota è conservata: riprova.';
            }
            clearTimer();
            break;
          }
          savedNote = writingNote;
          state.dirty = state.note !== savedNote;
          if (state.conflict && !state.dirty) {
            // Another editor committed after our write. There is no new local
            // draft to protect, so show that latest confirmed version directly.
            acceptRemote(state.remoteNote);
          } else {
            state.saved = !state.dirty && !state.conflict;
          }
        }
      } finally {
        state.saving = false;
        pending = null;
        forgetIfClean();
      }
    });
    return pending;
  };

  const edit = value => {
    if (!state.loaded || state.loading || state.loadError) return;
    state.note = String(value ?? '');
    state.dirty = state.note !== savedNote || (state.saving && state.note !== writingNote)
      || (state.conflict && state.note !== state.remoteNote);
    state.saved = false;
    state.saveError = '';
    clearTimer();
    if (state.conflict && state.note === state.remoteNote) acceptRemote(state.remoteNote);
    else if (state.dirty && !state.conflict) timer = setTimeout(flush, AUTOSAVE_MS);
  };

  const useSavedVersion = () => {
    if (!state.conflict || state.saving || state.loading || state.loadError) return;
    acceptRemote(state.remoteNote);
  };
  const saveMyVersion = () => {
    if (!state.conflict || state.saving || state.loading || state.loadError) return Promise.resolve();
    // Explicit user choice establishes a new comparison base. A change made
    // after this choice must still conflict rather than be silently overwritten.
    savedNote = state.remoteNote;
    state.conflict = false;
    state.remoteNote = '';
    state.dirty = state.note !== savedNote;
    return flush();
  };

  const controller = {
    acquire() {
      users += 1;
      if (users === 1) listen();
      let released = false;
      return {
        state,
        edit: value => { if (!released) edit(value); },
        flush: () => released ? Promise.resolve() : flush(),
        retryLoad: () => { if (!released) listen(); },
        retrySave: () => released ? Promise.resolve() : flush(),
        useSavedVersion: () => { if (!released) useSavedVersion(); },
        saveMyVersion: () => released ? Promise.resolve() : saveMyVersion(),
        release() {
          if (released) return;
          released = true;
          users -= 1;
          if (!users) stopListening();
          void flush();
          forgetIfClean();
        },
      };
    },
  };
  return controller;
}

export function acquireUserExpiringNote(userId) {
  const id = String(userId || '').trim();
  if (!id) throw new Error('Identificativo utente mancante.');
  if (!controllers.has(id)) controllers.set(id, createController(id));
  return controllers.get(id).acquire();
}
